import slugify from 'slugify';

import { TAG_LENGTH } from './validation/constants';

/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} s - The string to capitalize.
 * @returns {string} - The string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || '';

// Intl.Segmenter is the browser-standard word segmenter — it handles CJK, emoji, and
// mixed-script input, unlike whitespace splitting which undercounts languages without
// spaces between words.
const wordSegmenter = new Intl.Segmenter(undefined, { granularity: 'word' });

/** Counts word-like segments using Intl.Segmenter (handles CJK, emoji, mixed scripts). */
export const countWords = (value: string): number => {
  let count = 0;
  for (const segment of wordSegmenter.segment(value)) {
    if (segment.isWordLike) {
      count++;
    }
  }
  return count;
};

/**
 * Adds a dollar sign to a string if it doesn't already start with one.
 * For example: messi -> $messi
 */
export const checkForDollar = (str: string) => (str.startsWith('$') ? str : `$${str}`);

/**
 * Removes all the occurrences of the '$' symbol from a string.
 *
 * @param {string} input - The input string.
 * @returns {string} - The string without '$' symbols.
 */
export const removeDollarSign = (input: string) => (input || '').replace(/\$/g, '');

/** Sanitizes user input into a valid variable name that starts with a letter or underscore. */
export const sanitizeVariableName = (input: string): string => {
  let name = removeDollarSign(input);
  name = name.replace(/[^\w.-]+/g, '_');
  name = name.replace(/\.{2,}/g, '.');
  name = name.replace(/^[^a-zA-Z_]+/, '');
  name = name.replace(/[._]+$/, '');
  if (name.length > 0 && name.length < 2) {
    name = name + '_';
  }
  return name;
};

/** Sanitizes user input into a valid tag name matching `tagPattern` (`[\w][\w-]*`). */
export const sanitizeTagName = (input: string): string => {
  if (!input) {
    return '';
  }
  let name = input.replace(/[^\w-]+/g, '_');
  name = name.replace(/^[^\w]+/, '');
  name = name.replace(/[-_]+$/, '');
  return name.slice(0, TAG_LENGTH);
};

/**
 * Removes all the occurrences of the '@' symbol from a string.
 *
 * @param {string} input - The input string.
 * @returns {string} - The string without '@' symbols.
 */
export const removeAtSign = (input: string) => (input || '').replace(/@/g, '');

/**
 * Adds a dollar sign to the beginning of a string if it doesn't already have one.
 *
 * @param {string} input - The string to process.
 * @returns {string} - The processed string with a dollar sign.
 */
export const addDollarSign = (input: string) => {
  if (input?.startsWith('$') || input === '') {
    return input;
  } else {
    return '$' + input;
  }
};

/**
 * Wraps words that start with a dollar sign with curly braces.
 *
 * @param {string} input - The input string to be processed.
 * @returns {string} - The input string with all words starting with a dollar sign wrapped with curly braces.
 */
export const wrapDollarSignWithCurlyBraces = (input: string): string => {
  const regex = /\$[\w\d]+((\.|-)[\w\d]+)?/g; // Matches words starting with $ and optional .word part

  if (regex.test(input)) {
    const wrapped = input.replace(regex, '{{$&}}'); // Wrap all matched dollar signs with curly braces
    return wrapped;
  } else {
    return input; // If there is no dollar sign, return the original string
  }
};

/**
 * Decodes HTML entities in a string into their corresponding characters.
 *
 * This function creates a temporary DOM element (textarea), assigns the
 * provided text to its `innerHTML`, and then retrieves the decoded value
 * from the element's `value` property. This effectively converts HTML
 * entity codes (e.g., `&amp;`, `&#39;`) to their respective characters (e.g., `&`, `'`).
 *
 * @param {string} text - The string containing HTML entities that need to be decoded.
 * @returns {string} - The decoded string with HTML entities converted to their corresponding characters.
 *
 * @example
 * "I&#39;m transferring ..." => "I'm transferring ..."
 */
export const decodeHTMLEntities = (text: string) => {
  // Every entity starts with '&'; skipping the textarea allocation matters in list rows.
  if (!text || !text.includes('&')) {
    return text;
  }
  const txt = document.createElement('textarea');
  txt.innerHTML = text;
  return txt.value;
};

/**
 * Converts a name to a tag by slugifying and truncating it to a specified length.
 *
 * @param {string} str - The name to convert.
 * @returns {string | null} - The converted tag or null if the input is invalid.
 */
export const convertNameToTag = (str: string) => {
  if (!str) {
    return null;
  }
  return slugify(str, {
    replacement: '_', // Replace spaces with hyphens
    remove: /[^\w\s-]/g, // Remove all non-alphanumeric and non-hyphen characters
    lower: true, // Convert to lowercase
    strict: true, // Enforce strict mode
  }).slice(0, TAG_LENGTH);
};

/**
 * Regular expression for validating common symbols.
 */
export const commonSymbolsRegex = /^[a-zA-Z0-9\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/;

/**
 * Extracts the name from each item in the provided collection.
 *
 * @param {{ name: string }[]} [items=[]] - The items that contain names.
 * @returns {string[]} - An array with the extracted names.
 */
export const extractNames = (items: { name: string }[] = []): string[] =>
  items.map((item) => item.name);

/**
 * Returns an array of names from the provided items, excluding the specified name.
 *
 * @param {Array<unknown>} items - The array of items to extract names from.
 * @param {string} excludeName - The name to exclude from the returned array.
 * @param {string} [nameKey='name'] - The key to use to extract the name from each item. Defaults to 'name'.
 * @returns {Array<string>} - An array of names, excluding the specified name.
 *
 * @example
 * const data = [{name: 'John', age: 30}, {name: 'Jane', age: 25}, {name: 'Joe', age: 35}];
 * getRestrictedNames(data, 'Jane'); // Returns: ['John', 'Joe']
 * getRestrictedNames(data, 'John', 'name'); // Returns: ['Jane', 'Joe']
 */
export const getRestrictedNames = (
  items: Array<unknown>,
  excludeName: string,
  nameKey = 'name'
) => {
  if (!items || !excludeName) {
    return [];
  }

  return (
    items
      ?.map((item) => item[nameKey])
      .filter((name) => name.toLowerCase() !== excludeName.toLowerCase()) ?? []
  );
};

/**
 * Builds a unique name by appending an incremented number in parentheses.
 *
 * @param {string} originalName - The name to deduplicate.
 * @param {string[]} existingNames - List of existing names to check against.
 * @param {object} options - Pass `hasExtension: false` for names that are not file names, so a dot
 * in the middle of the name is not read as a file extension.
 * @returns {string} - A unique name that respects existing numbered variants.
 */
export const generateUniqueName = (
  originalName: string,
  existingNames: string[] = [],
  { hasExtension = true }: { hasExtension?: boolean } = {}
): string => {
  const trimmedOriginal = originalName?.trim() ?? '';

  if (!trimmedOriginal) {
    return trimmedOriginal;
  }

  const normalizedExisting = existingNames.map((name) => name?.trim() ?? '');
  const originalLower = trimmedOriginal.toLowerCase();
  const originalExists = normalizedExisting.some((name) => name.toLowerCase() === originalLower);

  if (!originalExists) {
    return trimmedOriginal;
  }

  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const splitNameAndExtension = (value: string) => {
    const lastDotIndex = hasExtension ? value.lastIndexOf('.') : -1;

    if (lastDotIndex <= 0 || lastDotIndex === value.length - 1) {
      return { base: value, extension: '' };
    }

    return { base: value.slice(0, lastDotIndex), extension: value.slice(lastDotIndex) };
  };

  const { base: rawBaseName, extension } = splitNameAndExtension(trimmedOriginal);
  const baseName = rawBaseName.replace(/ \(\d+\)$/, '').trim();
  const basePattern = baseName ? `${escapeRegExp(baseName)} ` : '';
  const baseRegex = new RegExp(`^${basePattern}\\((\\d+)\\)$`, 'i');
  const extensionLower = extension.toLowerCase();

  let highestNumber = 0;

  for (const name of normalizedExisting) {
    const { base: existingBase, extension: existingExtension } = splitNameAndExtension(name);

    if (existingExtension.toLowerCase() !== extensionLower) {
      continue;
    }

    const match = existingBase.match(baseRegex);
    if (match) {
      const number = parseInt(match[1], 10);
      if (!Number.isNaN(number)) {
        highestNumber = Math.max(highestNumber, number);
      }
    }
  }

  const nextNumber = highestNumber === 0 ? 1 : highestNumber + 1;
  const uniqueBase = baseName ? `${baseName} (${nextNumber})` : `(${nextNumber})`;
  return `${uniqueBase}${extension}`;
};
